/**
 * Date/number helpers and normalize for shop owner analytics.
 * Used by AnalyticsTab.jsx only.
 */

export const CURRENCY = (n) => Number(n || 0).toLocaleString('en-US');

export const toDate = (v) => {
  if (v == null) return null;
  if (v instanceof Date && !isNaN(v)) return v;
  if (typeof v === 'number') {
    const ms = v > 1e12 ? v : v * 1000;
    const d = new Date(ms);
    return isNaN(d) ? null : d;
  }
  if (typeof v === 'string') {
    const ymd = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) {
      const [, y, m, d] = ymd;
      const local = new Date(Number(y), Number(m) - 1, Number(d));
      if (!isNaN(local)) return local;
    }
    const iso = new Date(v);
    if (!isNaN(iso)) return iso;
    const m = v.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})(.*)?$/);
    if (m) {
      let [, a, b, c, rest] = m;
      if (c.length === 2) c = `20${c}`;
      const isDMY = Number(a) > 12 || (Number(a) <= 12 && Number(b) <= 12 ? false : true);
      const [dd, mm, yyyy] = isDMY ? [a, b, c] : [b, a, c];
      const normStr = `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}${rest || ''}`;
      const d2 = new Date(normStr);
      if (!isNaN(d2)) return d2;
    }
  }
  return null;
};

export const startOf = (d, unit) => {
  const x = new Date(d);
  if (unit === 'day') x.setHours(0, 0, 0, 0);
  if (unit === 'week') {
    const day = x.getDay();
    const diff = (day + 6) % 7;
    x.setDate(x.getDate() - diff);
    x.setHours(0, 0, 0, 0);
  }
  if (unit === 'month') {
    x.setDate(1);
    x.setHours(0, 0, 0, 0);
  }
  if (unit === 'year') {
    x.setMonth(0, 1);
    x.setHours(0, 0, 0, 0);
  }
  return x;
};

export const add = (d, unit, n) => {
  const x = new Date(d);
  if (unit === 'day') x.setDate(x.getDate() + n);
  if (unit === 'week') x.setDate(x.getDate() + n * 7);
  if (unit === 'month') x.setMonth(x.getMonth() + n);
  if (unit === 'year') x.setFullYear(x.getFullYear() + n);
  return x;
};

export const toDateKey = (d) => {
  if (!(d instanceof Date)) return '';
  if (Number.isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const isSame = (a, b, unit) => +startOf(a, unit) === +startOf(b, unit);
export const isSameOrAfter = (a, b, unit = 'day') => +startOf(a, unit) >= +startOf(b, unit);
export const isSameOrBefore = (a, b, unit = 'day') => +startOf(a, unit) <= +startOf(b, unit);

export const firstDefined = (obj, keys) => {
  for (const k of keys) if (obj && obj[k] != null) return obj[k];
  return undefined;
};

export const num = (v) => (Number.isFinite(+v) ? +v : 0);

export const normalize = (a) => {
  const dateRaw = firstDefined(a, ['selectedDate', 'date', 'start', 'startTime', 'when', 'datetime', 'created', 'appointmentDate']);
  const d = toDate(dateRaw);
  return {
    date: d,
    status: String(firstDefined(a, ['status', 'state']) ?? '').toLowerCase() || 'pending',
    serviceName: firstDefined(a, ['serviceName', 'service', 'title']) || 'Unknown',
    staff: firstDefined(a, ['staffName', 'staff', 'staffId']) || '',
    client: firstDefined(a, ['clientName', 'client', 'clientId']) || '',
    createdAt: toDate(firstDefined(a, ['createdAt', 'created', 'bookedAt'])),
    price: num(firstDefined(a, ['totalPrice', 'price', 'amount', 'net', 'total'])),
    source: firstDefined(a, ['source', 'channel', 'origin']),
    cancellationReason: firstDefined(a, ['cancellationReason', 'cancelReason', 'reason']),
  };
};

export const formatLabel = (d, unit) => {
  const fmt = (opts) => new Intl.DateTimeFormat(undefined, opts).format(d);
  if (unit === 'day') return fmt({ month: 'short', day: 'numeric' });
  if (unit === 'week') {
    const wStart = startOf(d, 'week');
    const y = wStart.getFullYear();
    const oneJan = new Date(y, 0, 1);
    const week = Math.ceil((((wStart - oneJan) / 86400000) + oneJan.getDay() + 1) / 7);
    return `W${week} ${y}`;
  }
  if (unit === 'month') return fmt({ month: 'short', year: '2-digit' });
  if (unit === 'year') return fmt({ year: 'numeric' });
  return '';
};
